import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { LoadClientLossesReportTask } from '../Tasks/LoadClientLossesReportTask'
import { LoadLogisticiansFinancialReportTask } from '../Tasks/LoadLogisticiansFinancialReportTask'
import { LoadLogisticiansInvolvementReportTask } from '../Tasks/LoadLogisticiansInvolvementReportTask'
import { samePeriod } from '../Helpers/reportPeriod'

import type { IClientLossesReport } from '../Contracts/IClientLossesReport'
import type { ILogisticiansFinancialReport, LogisticiansFinancialSortField } from '../Contracts/ILogisticiansFinancialReport'
import type { ILogisticiansInvolvementReport, LogisticiansInvolvementMetric } from '../Contracts/ILogisticiansInvolvementReport'
import type { IReportPeriod, IReportSort } from '../Contracts/IReportPeriod'

/** Текст, коли бек не пояснив, що сталося (ЗВ-09). */
export const REPORT_LOAD_ERROR = 'Не вдалося завантажити звіт'

type Loader<Data, Field extends string> = (period: IReportPeriod, sort: IReportSort<Field> | null) => Promise<Data>

/**
 * Стан одного звіту (`SPEC-027` §3.4): період, сортування, дані,
 * `isLoading`, помилка. Читачі — `observer`.
 *
 * - Період живе в адресі; сторінка лише передає його сюди (`setPeriod`).
 * - Сортування — одне поле, як приймає бек; `null` — порядок бека за
 *   замовчуванням. Зміна сортування чи періоду перечитує звіт.
 * - Помилка (ЗВ-09) — замість вічного скелетона: `error` з `serverMessage`, а
 *   попередні дані **прибираються** (К-07) — інакше таблиця за старий період
 *   стояла б під новим фільтром.
 * - Відповідь, що запізнилася (людина встигла змінити період), відкидається
 *   за номером запиту й нічого не перетирає.
 */
export class ReportStore<Data, Field extends string = never> {
    period: IReportPeriod | null = null
    sort: IReportSort<Field> | null = null
    data: Data | null = null
    isLoading = false
    error: string | null = null

    private requestId = 0

    constructor(private readonly loader: Loader<Data, Field>) {
        makeAutoObservable<ReportStore<Data, Field>, 'requestId' | 'loader'>(this, { requestId: false, loader: false })
    }

    /**
     * Відкриття сторінки або зміна періоду в адресі. Читаємо щоразу, навіть
     * той самий період: повернення на вкладку має показати свіжі цифри.
     * Дані іншого періоду одразу прибираємо — таблиця не бреше під новим фільтром.
     */
    setPeriod(period: IReportPeriod): Promise<void> {
        if (this.period !== null && !samePeriod(this.period, period)) this.data = null

        this.period = period

        return this.load()
    }

    setSort(sort: IReportSort<Field> | null): Promise<void> {
        if (this.sort?.field === sort?.field && this.sort?.direction === sort?.direction) return Promise.resolve()

        this.sort = sort

        return this.load()
    }

    /** «Повторити» після помилки. */
    reload(): Promise<void> {
        return this.load()
    }

    async load(): Promise<void> {
        if (this.period === null) return

        const id = ++this.requestId
        const period = this.period
        const sort = this.sort

        this.isLoading = true
        this.error = null

        try {
            const data = await this.loader(period, sort)

            runInAction(() => {
                if (id !== this.requestId) return

                this.data = data
            })
        } catch (error) {
            runInAction(() => {
                if (id !== this.requestId) return

                this.data = null
                this.error = serverMessage(error, REPORT_LOAD_ERROR)
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isLoading = false
            })
        }
    }
}

/** По стору на звіт: вкладки між звітами не скидають уже прочитане. */
export const logisticiansFinancialReportStore = new ReportStore<ILogisticiansFinancialReport, LogisticiansFinancialSortField>(
    LoadLogisticiansFinancialReportTask
)

export const logisticiansInvolvementReportStore = new ReportStore<ILogisticiansInvolvementReport, LogisticiansInvolvementMetric>(
    LoadLogisticiansInvolvementReportTask
)

export const clientLossesReportStore = new ReportStore<IClientLossesReport>((period) => LoadClientLossesReportTask(period))
